/**
 * railWidth — how wide the full-screen thread rail is, and who is allowed to
 * say so (owner ask, Miles 2026-09-20).
 *
 * THE FINDING. "264px" was written three times — ChatContainer's fullscreen
 * `padding-left` (which is the only reason a conversation side exists at all),
 * ThreadSidebarContainer's own `width`, and the hydration skeleton that has to
 * match it or the rail jumps when the store lands — and the `821px` media
 * query guarding the first of them was a hand-copy of
 * `MOBILE_VIEWPORT_MAX_PX + 1`. Four literals, no owner. This module is the
 * owner.
 *
 * THE COUPLING THAT DECIDED THE DEFAULT. ChatContainer is a CSS container
 * (`container-type: inline-size`), and size queries resolve against the
 * CONTENT box — so the rail's width is subtracted from every
 * `@container chat-pane (...)` rule and from the measurement behind
 * `headerRailTier()`. Widening the rail narrows the conversation and can fold
 * the chat header into a smaller rail. A flat wider default would have done
 * that to every laptop between roughly 1124 and 1160 CSS px. So the default
 * SCALES: `clamp(264px, 20vw, 360px)` only exceeds 264px above a 1320px
 * viewport, where the pane still has more than a thousand pixels — no screen
 * that has the full header rail today loses it, and the screens with room to
 * spare get a rail you can actually read thread titles in.
 *
 * A pure leaf: `breakpoints.ts` is the only import, so styles.ts, the host and
 * the drag hook can all read it without a cycle (the `headerRailTiers.ts`
 * idiom). The CSS string is BUILT from the numbers below rather than written
 * out beside them, so the stylesheet and the JS twin cannot drift.
 */
import { MOBILE_VIEWPORT_MAX_PX } from '../../styles/breakpoints';

/** The custom property every consumer reads. Declared by ChatContainer. */
export const RAIL_WIDTH_VAR = '--prest0n-rail-width';

/**
 * Below this viewport width the rail hides itself and the fullscreen pane
 * drops its left padding. `MOBILE_VIEWPORT_MAX_PX + 1`, derived rather than
 * restated — the 821 in styles.ts was the copy.
 */
export const RAIL_VISIBLE_MIN_VIEWPORT_PX = MOBILE_VIEWPORT_MAX_PX + 1;

/* ── the screen-aware default ─────────────────────────────────────────── */

/** Today's width, and the floor the default never goes below. */
export const RAIL_DEFAULT_MIN_PX = 264;
/** The viewport share the default tracks between the two bounds. */
export const RAIL_DEFAULT_VW = 20;
/** Where the default stops growing — past this the rail is just wide. */
export const RAIL_DEFAULT_MAX_PX = 360;

/** The stylesheet's default. Built from the three numbers above. */
export const RAIL_DEFAULT_WIDTH_CSS =
  `clamp(${RAIL_DEFAULT_MIN_PX}px, ${RAIL_DEFAULT_VW}vw, ${RAIL_DEFAULT_MAX_PX}px)`;

/**
 * What that clamp() resolves to, in JS. Callers pass `window.innerWidth` —
 * that is what `vw` measures; `clientWidth` disagrees by a scrollbar.
 *
 * CSS defines `clamp(min, val, max)` as `max(min, min(val, max))`; this is
 * that expression, not an approximation of it.
 */
export function defaultRailWidthPx(viewportWidth: number): number {
  const preferred = (Number(viewportWidth) || 0) * (RAIL_DEFAULT_VW / 100);
  return Math.max(RAIL_DEFAULT_MIN_PX, Math.min(preferred, RAIL_DEFAULT_MAX_PX));
}

/* ── the bounds a drag may not cross ──────────────────────────────────── */

/** Narrow enough to be a list of names, never narrower. */
export const RAIL_MIN_PX = 220;
/** Wide enough for a long thread title; past this it is a second pane. */
export const RAIL_MAX_PX = 480;

/**
 * The conversation never gets less room than the DOCKED pane it would
 * otherwise be — `CHAT_LAYOUT.DOCKED_WIDTH_PX`, which this file may not import
 * without dragging types.ts into a leaf. `railWidth.test.ts` pins the pair, so
 * moving the docked pane moves this with it.
 */
export const RAIL_MIN_PANE_PX = 504;

/**
 * Hold a width inside the bounds, given the room actually available. The
 * ceiling is viewport-aware so a drag can never starve the conversation; the
 * floor always wins a fight with it, because a rail narrower than RAIL_MIN_PX
 * is not a rail.
 *
 * NOT applied to the default: at the very bottom of the rail's range
 * (821-823px viewports) today's 264px already leaves the pane under this
 * floor, and quietly shaving 3px off it there would be a behaviour change
 * nobody asked for. The bounds exist to police the DRAG.
 */
export interface RailBounds { min: number; max: number; }

/**
 * The bounds in force at a given viewport. Exported because the separator
 * SPEAKS them (aria-valuemin / aria-valuemax), and a splitter that announces
 * a maximum it will not actually accept is lying to the person who cannot see
 * where it stopped.
 */
export function railWidthBounds(viewportWidth: number): RailBounds {
  return {
    min: RAIL_MIN_PX,
    max: Math.max(
      RAIL_MIN_PX,
      Math.min(RAIL_MAX_PX, (Number(viewportWidth) || 0) - RAIL_MIN_PANE_PX),
    ),
  };
}

export function clampRailWidth(px: number, viewportWidth: number): number {
  const { min, max } = railWidthBounds(viewportWidth);
  const value = Number(px);
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(Math.round(value), max));
}

/**
 * The width to render: the person's stored choice held inside the bounds, or
 * the screen-aware default when they have never expressed one.
 *
 * `null` in ⇒ `null` out, deliberately: "no stored choice" is not a width, and
 * the caller answers it by REMOVING the inline override so the stylesheet's
 * own clamp() serves — which keeps the default responsive to a window resize
 * without any JS at all.
 */
export function resolveRailWidth(stored: number | null, viewportWidth: number): number | null {
  if (stored === null || !Number.isFinite(stored)) return null;
  return clampRailWidth(stored, viewportWidth);
}
